import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Fragment, useId, useRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../cn';
import { useGoBack, useReturnTo } from '../hooks/useReturnTo';

/**
 * In-page navigation: tabs and segmented filters.
 *
 * Both are controlled components — the URL or the screen owns the selection —
 * because "which tab am I on" is state screens genuinely want to read, link to
 * and restore.
 */

// ─── Tabs ───────────────────────────────────────────────────────────────────

export interface TabItem<K extends string = string> {
  readonly key: K;
  readonly label: string;
  /** Small counter after the label, e.g. how many people are on the event. */
  readonly count?: number;
  /** Draws a hairline before this tab, to group what comes after it apart from what came before. */
  readonly dividerBefore?: boolean;
  /**
   * A step the record has not reached yet: still selectable, drawn lighter, so
   * the path ahead shows without reading as work to do.
   */
  readonly upcoming?: boolean;
}

export interface TabsProps<K extends string = string> {
  readonly items: readonly TabItem<K>[];
  readonly active: K;
  readonly onChange: (key: K) => void;
  /** Names the tab list for screen readers, e.g. "Event sections". */
  readonly label: string;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
}

/**
 * The portal's tab bar: underline style on a hairline rule, brand-coloured
 * active state.
 *
 * Implements the WAI-ARIA tabs keyboard contract (arrow keys move and select,
 * Home/End jump) rather than leaving tabs as a row of buttons — the difference
 * is invisible to a mouse user and the whole interaction for a keyboard user.
 *
 * @param props - Items, the active key, and the change handler.
 * @returns The tab bar. Panels are the caller's, associated by `aria-controls`.
 */
export function Tabs<K extends string>({
  items,
  active,
  onChange,
  label,
  guide,
}: TabsProps<K>): React.ReactElement {
  const baseId = useId();
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * Moves selection with the keyboard, wrapping at the ends.
   *
   * @param event - The keydown event on the tab list.
   */
  function onKeyDown(event: React.KeyboardEvent): void {
    const index = items.findIndex((item) => item.key === active);
    let next = -1;

    if (event.key === 'ArrowRight') next = (index + 1) % items.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else return;

    event.preventDefault();
    const item = items[next];
    if (item === undefined) return;
    onChange(item.key);
    // Focus follows selection, per the ARIA authoring pattern.
    listRef.current
      ?.querySelector<HTMLButtonElement>(`#${CSS.escape(`${baseId}-tab-${item.key}`)}`)
      ?.focus();
  }

  return (
    <div
      data-guide={guide}
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="mb-5 flex flex-wrap gap-1 border-b border-portal-line"
    >
      {items.map((item) => {
        const selected = item.key === active;
        return (
          <Fragment key={item.key}>
          {item.dividerBefore ? (
            <span role="presentation" aria-hidden className="mx-2 my-2 w-px shrink-0 self-stretch bg-portal-line" />
          ) : null}
          <button
            id={`${baseId}-tab-${item.key}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`${baseId}-panel-${item.key}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.key)}
            className={cn(
              '-mb-px shrink-0 border-b-2 px-3.5 py-2.5 text-portal-small whitespace-nowrap transition-colors',
              selected
                ? 'border-brand font-bold text-brand'
                : 'border-transparent text-portal-muted hover:text-portal-ink',
              item.upcoming === true && !selected ? 'opacity-55' : null,
            )}
          >
            {item.label}
            {item.upcoming === true ? <span className="sr-only"> (not reached yet)</span> : null}
            {item.count === undefined ? null : (
              <span
                className={cn(
                  'ml-1.5 inline-block rounded-portal-pill px-1.5 text-portal-micro',
                  selected ? 'bg-brand-tint text-brand' : 'bg-slate-100 text-slate-600',
                )}
              >
                {item.count}
              </span>
            )}
          </button>
          </Fragment>
        );
      })}
    </div>
  );
}

// ─── Path ───────────────────────────────────────────────────────────────────

/** How far each chevron's point reaches into the next one. */
const PATH_ARROW = '12px';

/** Chevron outlines: the first segment has a flat back, every one has a point. */
function chevron(first: boolean): string {
  const point = `calc(100% - ${PATH_ARROW}) 0, 100% 50%, calc(100% - ${PATH_ARROW}) 100%`;
  return first ? `polygon(0 0, ${point}, 0 100%)` : `polygon(0 0, ${point}, 0 100%, ${PATH_ARROW} 50%)`;
}

export interface PathControlProps<K extends string = string> {
  readonly options: readonly SegmentedOption<K>[];
  readonly value: K;
  readonly onChange: (key: K) => void;
  /** Names the group for screen readers, e.g. "Which stage to show". */
  readonly label: string;
  readonly guide?: string | undefined;
}

/**
 * A filter over steps that run in order, drawn as chevrons that fit into
 * each other (Salesforce's "Path"): opportunity stages, a pipeline. Behaves
 * like {@link SegmentedControl}; use that when the options have no order.
 *
 * @param props - Ordered options, current value, change handler.
 * @returns The control, scrolling sideways when it doesn't fit.
 */
export function PathControl<K extends string>({ options, value, onChange, label, guide }: PathControlProps<K>): React.ReactElement {
  return (
    <div data-guide={guide} role="group" aria-label={label} className="max-w-full overflow-x-auto pb-1">
      <div className="flex min-w-max">
        {options.map((option, index) => {
          const selected = option.key === value;
          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={selected}
              disabled={option.disabled === true}
              title={option.disabled === true ? option.disabledReason : undefined}
              onClick={() => onChange(option.key)}
              style={{ clipPath: chevron(index === 0), marginLeft: index === 0 ? 0 : `calc(${PATH_ARROW} * -1 + 3px)` }}
              className={cn(
                'whitespace-nowrap py-2 text-portal-small transition-colors pointer-coarse:min-h-10',
                index === 0 ? 'rounded-l-portal-button pl-4 pr-6' : 'pl-7 pr-6',
                selected ? 'bg-brand font-bold text-white' : 'bg-portal-line/70 text-portal-ink hover:bg-portal-line',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                'disabled:cursor-not-allowed disabled:opacity-40',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Segmented control ──────────────────────────────────────────────────────

export interface SegmentedOption<K extends string = string> {
  readonly key: K;
  readonly label: string;
  /** Shown instead on phones ("1y" for "12 months") so many options still fit; screen readers get `label`. */
  readonly shortLabel?: string;
  /** Shown but not choosable, e.g. Monthly for a seven-day range. */
  readonly disabled?: boolean;
  /** Why it is disabled, shown on hover. */
  readonly disabledReason?: string;
}

export interface SegmentedControlProps<K extends string = string> {
  readonly options: readonly SegmentedOption<K>[];
  readonly value: K;
  readonly onChange: (key: K) => void;
  /** Names the group for screen readers, e.g. "Which events to show". */
  readonly label: string;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
}

/**
 * A small mutually-exclusive filter — Upcoming / Past / All.
 *
 * Distinct from {@link Tabs} on purpose: tabs change which *panel* is shown,
 * a segmented control changes a *query* over the same panel. Blurring the two
 * is how filters end up in the URL twice.
 *
 * @param props - Options, current value, change handler.
 * @returns The control.
 */
export function SegmentedControl<K extends string>({
  options,
  value,
  onChange,
  label,
  guide,
}: SegmentedControlProps<K>): React.ReactElement {
  return (
    <div
      data-guide={guide}
      role="group"
      aria-label={label}
      // inline-flex, not flex: the control must hug its options — as a
      // direct child of a stacked layout a block-level flex row would
      // stretch the pill bar across the whole page. max-w-full + flex-wrap
      // so a wide option set wraps onto a second line on phones: a sideways
      // pan inside the pill hid its last options behind a clipped label.
      className="inline-flex max-w-full flex-wrap rounded-portal-button border border-portal-line bg-white p-0.5"
    >
      {options.map((option) => {
        const selected = option.key === value;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={selected}
            disabled={option.disabled === true}
            title={option.disabled === true ? option.disabledReason : undefined}
            onClick={() => onChange(option.key)}
            className={cn(
              // Tighter on phones so a five-option range still fits a 375px screen;
              // 40px tall on touch screens so each option is a finger-sized target.
              'rounded-[5px] px-2.5 py-1.5 text-portal-small whitespace-nowrap transition-colors pointer-coarse:min-h-10 sm:px-3',
              selected ? 'bg-brand font-bold text-white' : 'text-portal-muted hover:text-portal-ink',
              'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-portal-muted',
            )}
          >
            {option.shortLabel ? (
              <>
                <span aria-hidden className="sm:hidden">
                  {option.shortLabel}
                </span>
                <span className="max-sm:sr-only">{option.label}</span>
              </>
            ) : (
              option.label
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Pagination ─────────────────────────────────────────────────────────────

export interface PaginationProps {
  /** Current 1-based page. */
  readonly page: number;
  readonly totalPages: number;
  /** Called with the page to move to. */
  readonly onChange: (page: number) => void;
  /**
   * Optional summary, e.g. "1–25 of 312". Rendered on the left; when absent
   * the control shows "Page X of Y".
   */
  readonly summary?: string;
  readonly className?: string;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
}

/**
 * Previous / next pager with a page indicator — the one pagination control
 * every list uses, so paging looks and behaves the same everywhere. Renders
 * nothing when there is a single page.
 *
 * @param props - Current page, total pages, and the change handler.
 * @returns The pager, or null when there is nothing to page.
 */
export function Pagination({
  page,
  totalPages,
  onChange,
  summary,
  className,
  guide,
}: PaginationProps): React.ReactElement | null {
  if (totalPages <= 1) return null;

  const button =
    'flex h-9 items-center rounded-portal-button border border-portal-line bg-white px-3 ' +
    'text-portal-small text-portal-ink shadow-portal-button transition-colors ' +
    'enabled:hover:border-brand enabled:hover:text-brand ' +
    'disabled:cursor-not-allowed disabled:text-portal-muted disabled:opacity-50';

  return (
    <nav
      data-guide={guide}
      aria-label="Pagination"
      className={cn('mt-4 flex items-center justify-between gap-4', className)}
    >
      <span className="text-portal-micro text-portal-muted">
        {summary ?? `Page ${page} of ${totalPages}`}
      </span>
      <span className="flex items-center gap-2">
        <button
          type="button"
          className={button}
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeftIcon aria-hidden className="size-3.5 shrink-0" />
          Previous
        </button>
        <button
          type="button"
          className={button}
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
        >
          Next
          <ChevronRightIcon aria-hidden className="size-3.5 shrink-0" />
        </button>
      </span>
    </nav>
  );
}

// ─── Breadcrumbs ────────────────────────────────────────────────────────────

export interface BreadcrumbItem {
  readonly label: string;
  /** Where the crumb goes. Omit for a section with no page of its own, and for the current page. */
  readonly to?: string;
}

export interface BreadcrumbsProps {
  /**
   * The section path, outermost first: "Administration / Teams". It names where
   * a page sits, not the record itself, so a detail page stops at its list
   * (the title already names the record) and a list page ends with itself.
   */
  readonly items: readonly BreadcrumbItem[];
  readonly className?: string;
}

/**
 * Where a page sits, in the eyebrow's uppercase style: "ADMINISTRATION / TEAMS".
 * Replaces the eyebrow (design-system rule, 2026-09-22), via `PageHeading`'s
 * `breadcrumbs`. A crumb with a page is a link; the last crumb without one is
 * the current page.
 *
 * @param props - The trail, outermost first.
 * @returns The breadcrumb trail.
 */
export function Breadcrumbs({ items, className }: BreadcrumbsProps): React.ReactElement {
  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-1.5 text-portal-micro font-extrabold tracking-[1.2px] text-brand-link uppercase">
        {items.map((item, index) => {
          const current = index === items.length - 1 && item.to === undefined;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-1.5">
              {index === 0 ? null : <span aria-hidden="true">/</span>}
              {item.to === undefined ? (
                <span aria-current={current ? 'page' : undefined}>{item.label}</span>
              ) : (
                <Link to={item.to} className="underline-offset-2 hover:underline">
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ─── Back link ──────────────────────────────────────────────────────────────

export interface BackLinkProps {
  /** Where "back" goes when the page was opened directly; otherwise it returns to its origin. */
  readonly to: string;
  /** The destination's name, e.g. "All events". Rendered after the arrow. */
  readonly children: React.ReactNode;
  readonly className?: string;
}

/**
 * The "← back to the list" link that sits above every detail page. One
 * component so the arrow, spacing and hover are identical everywhere.
 *
 * @param props - Destination and label.
 * @returns The back link.
 */
export function BackLink({ to, children, className }: BackLinkProps): React.ReactElement {
  // Goes back to wherever this page was opened from, when that is known;
  // `to` is the fallback for a page opened directly.
  const href = useReturnTo(to);
  const goBack = useGoBack(to);
  return (
    <Link
      to={href}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        goBack();
      }}
      className={cn(
        'inline-flex items-center gap-1.5 text-portal-small text-brand underline-offset-2 hover:underline',
        className,
      )}
    >
      <ArrowLeftIcon aria-hidden className="size-3.5 shrink-0" />
      {children}
    </Link>
  );
}
